import api from './api';

/**
 * Admin Service
 * 
 * NOTE: api.js interceptor already extracts axios's response.data, 
 * so 'response' here IS the API body: { success: true, data: ..., pagination?: ... }
 * We return the FULL response object so consumers can access both data and pagination.
 */
const adminService = {
  // ==========================================
  // ORGANIZATION MANAGEMENT
  // ==========================================
  
  getAllOrganizations: async (params = {}) => {
    // Returns { success, data: [...], pagination: {...} }
    const response = await api.get('/admin/organizations', { params });
    return response;
  },

  getOrganizationById: async (id) => {
    // Returns { success, data: {...} }
    const response = await api.get(`/admin/organizations/${id}`);
    return response;
  },

  createOrganization: async (data) => {
    const response = await api.post('/admin/organizations', data);
    return response;
  },

  updateOrganization: async (id, data) => {
    const response = await api.put(`/admin/organizations/${id}`, data);
    return response;
  },

  sendOrganizationCredentials: async (id, data) => {
    const response = await api.post(`/admin/organizations/${id}/send-credentials`, data);
    return response;
  },

  deleteOrganization: async (id, hardDelete = false) => {
    const response = await api.delete(`/admin/organizations/${id}`, {
      params: { hardDelete },
    });
    return response;
  },

  // ==========================================
  // ADMIN USER MANAGEMENT
  // ==========================================

  getAllAdminUsers: async (showInactive = false) => {
    // Returns { success, data: [...] }
    const response = await api.get('/admin/users', {
      params: { showInactive },
    });
    return response;
  },

  createAdminUser: async (data) => {
    const response = await api.post('/admin/users', data);
    return response;
  },

  updateAdminUser: async (id, data) => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response;
  },

  sendAdminUserCredentials: async (id, data = {}) => {
    const response = await api.post(`/admin/users/${id}/send-credentials`, data);
    return response;
  },

  toggleAdminStatus: async (id) => {
    const response = await api.put(`/admin/users/${id}/toggle-status`);
    return response;
  },

  // ==========================================
  // DASHBOARD STATS
  // ==========================================

  getPlatformStats: async (params = {}) => {
    // Returns { success, data: { organizations, locations, users, ..., recentActivityPagination } }
    const response = await api.get('/admin/dashboard/stats', { params });
    return response;
  },

  getOrganizationStats: async (orgId) => {
    // Returns { success, data: {...} }
    const response = await api.get(`/admin/dashboard/stats/${orgId}`);
    return response;
  },

  // ==========================================
  // ORGANIZATION INQUIRIES
  // ==========================================

  getInquiries: async (params = {}) => {
    const response = await api.get('/admin/inquiries', { params });
    return response;
  },

  getInquiryById: async (id) => {
    const response = await api.get(`/admin/inquiries/${id}`);
    return response;
  },

  updateInquiry: async (id, data) => {
    const response = await api.put(`/admin/inquiries/${id}`, data);
    return response;
  },

  getInquiryStats: async () => {
    const response = await api.get('/admin/inquiries/stats');
    return response;
  },

  convertInquiry: async (id, data = {}) => {
    const response = await api.post(`/admin/inquiries/${id}/convert`, data);
    return response;
  },

  // ==========================================
  // AGENT MANAGEMENT
  // ==========================================

  getAllAgents: async (params = {}) => {
    const response = await api.get('/admin/agents', { params });
    return response;
  },

  getAgentById: async (id) => {
    const response = await api.get(`/admin/agents/${id}`);
    return response;
  },

  createAgent: async (data) => {
    const response = await api.post('/admin/agents', data);
    return response;
  },

  updateAgent: async (id, data) => {
    const response = await api.put(`/admin/agents/${id}`, data);
    return response;
  },

  deleteAgent: async (id) => {
    const response = await api.delete(`/admin/agents/${id}`);
    return response;
  },

  toggleAgentStatus: async (id) => {
    const response = await api.put(`/admin/agents/${id}/toggle-status`);
    return response;
  },

  sendAgentCredentials: async (id, data = {}) => {
    const response = await api.post(`/admin/agents/${id}/send-credentials`, data);
    return response;
  },

  // ==========================================
  // TEAM LEADER MANAGEMENT
  // ==========================================

  getAllTeamLeaders: async (params = {}) => {
    const response = await api.get('/admin/team-leaders', { params });
    return response;
  },

  getTeamLeaderById: async (id) => {
    const response = await api.get(`/admin/team-leaders/${id}`);
    return response;
  },

  createTeamLeader: async (data) => {
    const response = await api.post('/admin/team-leaders', data);
    return response;
  },

  updateTeamLeader: async (id, data) => {
    const response = await api.put(`/admin/team-leaders/${id}`, data);
    return response;
  },

  deleteTeamLeader: async (id) => {
    const response = await api.delete(`/admin/team-leaders/${id}`);
    return response;
  },

  toggleTeamLeaderStatus: async (id) => {
    const response = await api.put(`/admin/team-leaders/${id}/toggle-status`);
    return response;
  },

  sendTeamLeaderCredentials: async (id, data = {}) => {
    const response = await api.post(`/admin/team-leaders/${id}/send-credentials`, data);
    return response;
  },

  getAllLocationsFlat: async () => {
    const response = await api.get('/admin/all-locations');
    return response;
  },

  // ==========================================
  // ADMIN CONTEXT (org/location selection)
  // ==========================================

  getAdminContext: async () => {
    // Returns { success, data: { organizations, locations, selectedOrganizationId, selectedLocationId } }
    const response = await api.get('/admin/context');
    return response;
  },

  setAdminContext: async (data) => {
    const response = await api.put('/admin/context', data);
    return response;
  },

  getAllLocations: async (params = {}) => {
    const response = await api.get('/admin/locations', { params });
    console.log("response", response);
    return response || {};
  },

  getAllUserLogs: async (params = {}) => {
    const response = await api.get('/admin/user-logs', { params });
    return response;
  },
  getUserLogStats: async (params = {}) => {
    const response = await api.get("/admin/user-logs/stats", { params });
    return response;
  },

  deleteAllUserLogs: async () => {
    const response = await api.delete("/admin/user-logs");
    return response;
  },

  getIntegrationSyncLogs: async (params = {}) => {
    const response = await api.get('/admin/integration-sync-logs', { params });
    return response;
  },

  getIntegrationSyncCronStatus: async () => {
    const response = await api.get('/admin/integration-sync-logs/cron-status');
    return response;
  },

  deleteIntegrationSyncLogs: async (params = {}) => {
    const response = await api.delete('/admin/integration-sync-logs', { params });
    return response;
  },
};

export default adminService;
